import { useCallback, useState } from "react";
import { isValid, parseISO } from "date-fns";

import {
  COMPARE_MODES,
  RANGE_PRESETS,
  buildCompareRange,
  defaultDateSelection,
  toIsoDate,
} from "../components/MetricsDateRangeBar.jsx";

const CUSTOM_PRESET = "custom";

const toIsoRange = (range) =>
  range?.from && range?.to ? { from: toIsoDate(range.from), to: toIsoDate(range.to) } : null;

const parseRange = (range) => {
  if (!range?.from || !range?.to) return null;
  const from = parseISO(range.from);
  const to = parseISO(range.to);
  return isValid(from) && isValid(to) ? { from, to } : null;
};

// Presets are stored by name and rebuilt on load, so "Año hasta hoy" still
// means today's year-to-date tomorrow; only hand-picked ranges keep their dates.
const serialize = (selection) => ({
  preset: selection.preset,
  range: selection.preset === CUSTOM_PRESET ? toIsoRange(selection.range) : null,
  compareEnabled: selection.compareEnabled,
  compareMode: selection.compareMode,
  compareRange:
    selection.compareMode === COMPARE_MODES.CUSTOM ? toIsoRange(selection.compareRange) : null,
  showPreviousValues: selection.showPreviousValues,
});

const restore = (stored, presets) => {
  const fallback = defaultDateSelection(presets);
  const preset = presets.find((item) => item.value === stored.preset);
  const range = preset ? preset.build() : parseRange(stored.range);
  if (!range) return fallback;

  const storedMode = Object.values(COMPARE_MODES).includes(stored.compareMode)
    ? stored.compareMode
    : COMPARE_MODES.PREVIOUS;
  const customCompare =
    storedMode === COMPARE_MODES.CUSTOM ? parseRange(stored.compareRange) : null;
  const compareMode =
    storedMode === COMPARE_MODES.CUSTOM && !customCompare ? COMPARE_MODES.PREVIOUS : storedMode;

  return {
    range,
    preset: preset ? preset.value : CUSTOM_PRESET,
    compareEnabled: stored.compareEnabled ?? fallback.compareEnabled,
    compareMode,
    compareRange: customCompare || buildCompareRange(range, compareMode),
    showPreviousValues: stored.showPreviousValues ?? fallback.showPreviousValues,
  };
};

const read = (storageKey, presets) => {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? restore(JSON.parse(raw), presets) : defaultDateSelection(presets);
  } catch {
    return defaultDateSelection(presets);
  }
};

/** A `MetricsDateRangeBar` selection that survives tab, client and page changes. */
export function usePersistedDateSelection(storageKey, presets = RANGE_PRESETS) {
  const [selection, setSelection] = useState(() => read(storageKey, presets));

  const update = useCallback(
    (next) => {
      setSelection(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify(serialize(next)));
      } catch {
        // Storage full or blocked: the selection still applies for this visit.
      }
    },
    [storageKey]
  );

  return [selection, update];
}

export default usePersistedDateSelection;
